import React, { FC } from 'react';
import { isSearchInvalid } from 'app/functions/general';
import { Input } from '@flixbus/honeycomb-react';
import { Icon, IconArrowDown } from 'app/icons';
import { useTranslations } from 'app/hooks/useTranslations';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import { KEY_NAMES } from 'app/providers/keyNames';
import * as styles from './ProductSummary.scss';
import { TextWithEllipsis } from 'app/components/ui/TextWithEllipsis/TextWithEllipsis';
import { VoButton } from 'app/components/ui/VoButton/VoButton';
import { ProductsId } from '@webc/meeseeks-ui-sdk';

export type ProductSummaryProps = {
  value: string;
  label: string;
  passengerCount: number;
  onFocus?: () => void;
  onClick?: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  isExpanded: boolean;
  wheelchairsCount?: number;
  notFulfilledProductDependencies: ProductsId[];
};
export const ProductSummary: FC<ProductSummaryProps> = ({
  value,
  label,
  passengerCount,
  onClick,
  inputRef,
  isExpanded,
  wheelchairsCount,
  notFulfilledProductDependencies,
}) => {
  const {
    getInputAriaLabel,
    getPassengerAmountValidationLabel,
    getWheelchairValidationLabel,
    getTranslation,
  } = useTranslations(
    {
      getPassengerAmountValidationLabel:
        'search_bundle.search_widget.passengers.please_add_passengers_placeholder',
      getWheelchairValidationLabel: 'search_mask.validation.wheelchair',
      getInputAriaLabel: a11yLabelsKeys.products.input,
    },
    true,
  );

  const keyDownHandler = (e: React.KeyboardEvent) => {
    if ((e.key === KEY_NAMES.ENTER || e.key === KEY_NAMES.SPACE) && onClick) {
      onClick();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const isValid = !isSearchInvalid({
    passengerCount,
    wheelchairsCount,
    notFulfilledProductDependencies,
  });
  const ariaLabel = isValid ? `${value}, ${getInputAriaLabel()}` : getInputAriaLabel();
  const getInfoError = () => {
    if (wheelchairsCount && wheelchairsCount > 0) {
      return getWheelchairValidationLabel();
    }
    if (notFulfilledProductDependencies?.length > 0) {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: getTranslation(`search_mask.validation.${notFulfilledProductDependencies[0]}`),
          }}
        />
      );
    }
    return getPassengerAmountValidationLabel();
  };

  return (
    <div className={styles.wrapper}>
      <VoButton
        ariaLabel={ariaLabel}
        onClick={onClick}
        ariaControls="dialog-products"
        isExpanded={isExpanded}
      >
        {value}
      </VoButton>
      <Input
        readOnly={true}
        tabIndex={-1}
        aria-hidden={true}
        id="productSummary"
        value={value}
        infoError={getInfoError()}
        label={
          <span aria-hidden={true}>
            <TextWithEllipsis text={label || '...'} />
          </span>
        }
        onClick={onClick}
        onKeyDown={keyDownHandler}
        valid={!isValid ? false : undefined}
        innerRef={inputRef}
        iconRight={
          isValid ? (
            <Icon aria-hidden={true} appearance="primary" InlineIcon={IconArrowDown} />
          ) : undefined
        }
        extraClasses={styles.input}
        aria-expanded={isExpanded}
      />
    </div>
  );
};
